import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid,
  Chip, Alert, CircularProgress,
  Button, Tooltip, IconButton, useTheme,
  Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress,
  Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import {
  Assessment, TrendingUp, BarChart, TableChart,
  Refresh, GetApp, CheckCircle, Science, CalendarToday, PlayArrow
} from '@mui/icons-material';
import axios from 'axios';

// Types
interface RegressionResult {
  category: string;
  category_display_name: string;
  overall_fit: { multiple_r: number; r_square: number; adjusted_r_square: number; standard_error: number; observations: number; };
  anova: { df_regression: number; df_residual: number; df_total: number; ss_regression: number; ss_residual: number; ss_total: number; ms_regression: number; ms_residual: number; f_statistic: number; f_p_value: number; significant: boolean; };
  coefficients: { intercept: { value: number; standard_error: number; t_statistic: number; p_value: number; confidence_interval_95: [number, number]; }; slope: { value: number; standard_error: number; t_statistic: number; p_value: number; confidence_interval_95: [number, number]; }; };
  data_points: number; analysis_timestamp: string; regression_equation: string; error?: string;
}

interface AnalysisSummary {
  total_categories: number; analyzed_categories: number; successful_analyses: number; failed_analyses: number; analysis_timestamp: string;
  categories_by_r_squared: Array<{ category: string; display_name: string; r_squared: number; significant: boolean; }>;
  significant_regressions: number; data_source?: string;
}

interface RegressionStatus {
  year: number; status: 'no_master_data' | 'needs_calculation' | 'processing' | 'ready' | 'partial';
  message: string; has_analysis: boolean; has_master_data: boolean; master_data_count: number;
  progress?: number; categories_processed?: number; total_categories?: number; categories_count?: number; last_calculated?: string;
}

// Helper Components
const YearFilterCard: React.FC<{ yearFilter: number | ''; availableYears: number[]; onYearChange: (year: number) => void; status?: RegressionStatus; onRun?: () => void; isCalculating?: boolean }> = ({ yearFilter, availableYears, onYearChange, status, onRun, isCalculating }) => (
  <Card sx={{ border: '2px solid', borderColor: 'primary.main', mb: 3 }}>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        <CalendarToday sx={{ fontSize: 40, color: 'primary.main' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Filter Analysis by Year</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Year</InputLabel>
              <Select value={yearFilter} label="Year" onChange={(e) => onYearChange(e.target.value as number)}>
                {availableYears.map((year) => <MenuItem key={year} value={year}>{year}</MenuItem>)}
              </Select>
            </FormControl>
            {status?.has_master_data && <Chip label={`${status.master_data_count} records`} color="info" />}
            <Button 
              variant="contained" 
              size="small" 
              startIcon={<PlayArrow />} 
              onClick={onRun} 
              disabled={isCalculating}
              color={status?.has_analysis ? "secondary" : "primary"}
            >
              {isCalculating ? 'Running...' : (status?.has_analysis ? 'Re-run' : 'Run Regression')}
            </Button>
            {status?.has_analysis && <Chip label="Analysis Ready" color="success" />}
          </Box>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const CalculationDialog: React.FC<{ open: boolean; calcYear: number | null; calcProgress: number; calcMessage: string; isCalculating: boolean; onClose: () => void }> = ({ open, calcYear, calcProgress, calcMessage, isCalculating, onClose }) => (
  <Dialog open={open} maxWidth="sm" fullWidth>
    <DialogTitle>Running Regression Analysis</DialogTitle>
    <DialogContent>
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Processing year {calcYear}...</Typography>
        <LinearProgress variant="determinate" value={calcProgress} sx={{ height: 10, borderRadius: 5, mb: 2 }} />
        <Typography variant="body2" sx={{ mb: 2 }}>{calcProgress}% complete</Typography>
        <Alert severity={isCalculating ? "info" : "success"}>{calcMessage || (isCalculating ? 'Calculating...' : 'Complete!')}</Alert>
      </Box>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={isCalculating}>{isCalculating ? 'Please wait...' : 'Close'}</Button>
    </DialogActions>
  </Dialog>
);

const SummaryCards: React.FC<{ summary: AnalysisSummary | null; theme: any }> = ({ summary, theme }) => {
  if (!summary) return null;
  const cards = [
    { label: 'Successful Analyses', value: summary.successful_analyses, icon: <CheckCircle />, color: theme.palette.primary },
    { label: 'Significant Regressions', value: summary.significant_regressions, icon: <TrendingUp />, color: theme.palette.success },
    { label: 'Best R Score', value: `${((summary.categories_by_r_squared?.[0]?.r_squared || 0) * 100).toFixed(1)}%`, icon: <BarChart />, color: theme.palette.warning },
    { label: 'Total Categories', value: summary.total_categories, icon: <TableChart />, color: theme.palette.info },
  ];
  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {cards.map((card, i) => (
        <Grid item xs={12} sm={6} md={3} key={i}>
          <Card sx={{ background: `linear-gradient(135deg, ${card.color.main} 0%, ${card.color.dark} 100%)`, color: 'white' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box><Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>{card.value}</Typography><Typography variant="body2" sx={{ opacity: 0.9 }}>{card.label}</Typography></Box>
                {React.cloneElement(card.icon, { sx: { fontSize: 32, opacity: 0.8 } })}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

const ResultCard: React.FC<{ result: RegressionResult }> = ({ result }) => {
  const [expanded, setExpanded] = useState(false);
  const formatPValue = (pValue: number) => { if (pValue === 0) return '0.000000'; if (pValue < 1e-10) return pValue.toExponential(3); if (pValue < 0.001) return pValue.toFixed(8); return pValue.toFixed(4); };
  const formatNumber = (num: number) => num.toFixed(6);
  const formatPercentage = (num: number) => `${(num * 100).toFixed(2)}%`;
  const getRSquaredColor = (rSquared: number) => rSquared >= 0.8 ? 'success' : rSquared >= 0.6 ? 'warning' : 'error';
  const getPValueColor = (pValue: number) => pValue < 0.05 ? 'success' : pValue < 0.1 ? 'warning' : 'error';

  if (result.error) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}><Assessment sx={{ mr: 1, color: 'primary.main' }} /><Typography variant="h6" sx={{ fontWeight: 600 }}>{result.category_display_name}</Typography></Box>
          <Alert severity="error">{result.error}</Alert>
        </CardContent>
      </Card>
    );
  }

  const { overall_fit, coefficients } = result;
  const intercept = coefficients?.intercept?.value || 0;
  const slope = coefficients?.slope?.value || 0;

  return (
    <Card sx={{ height: '100%', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { boxShadow: 4 } }} onClick={() => setExpanded(!expanded)}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}><Assessment sx={{ mr: 1, color: 'primary.main' }} /><Typography variant="h6" sx={{ fontWeight: 600 }}>{result.category_display_name}</Typography></Box>
          <Chip label={expanded ? '▲' : '▼'} size="small" color="default" />
        </Box>

        {/* Summary Row */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip label={`R²: ${formatPercentage(overall_fit.r_square)}`} color={getRSquaredColor(overall_fit.r_square)} size="small" />
          <Chip label={`P-Value: ${formatPValue(result.anova.f_p_value)}`} color={getPValueColor(result.anova.f_p_value)} size="small" />
        </Box>

        {/* Expanded Details */}
        {expanded && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Regression Details</Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">R Square</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatNumber(overall_fit.r_square)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Adjusted R²</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatNumber(overall_fit.adjusted_r_square)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">P-Value</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, color: result.anova.f_p_value < 0.05 ? 'success.main' : 'text.primary' }}>{formatPValue(result.anova.f_p_value)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Observations</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{result.data_points}</Typography>
              </Box>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Coefficients</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Intercept</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatNumber(intercept)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Coefficient (Slope)</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatNumber(slope)}</Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ mt: 2, p: 1, bgcolor: 'white', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">Regression Equation</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                y = {formatNumber(slope)}x + {formatNumber(intercept)}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Footer */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ cursor: 'pointer' }}>
            {result.data_points} observations {expanded ? '(click to collapse)' : '(click to expand)'}
          </Typography>
          {result.anova.significant && <Chip label="Significant" color="success" size="small" />}
        </Box>
      </CardContent>
    </Card>
  );
};

const RegressionAnalysis: React.FC = () => {
  const theme = useTheme();
  const [results, setResults] = useState<Record<string, RegressionResult>>({});
  const [summary, setSummary] = useState<AnalysisSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<RegressionStatus | undefined>(undefined);
  const [yearFilter, setYearFilter] = useState<number | ''>('');
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [calcDialogOpen, setCalcDialogOpen] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calcProgress, setCalcProgress] = useState(0);
  const [calcMessage, setCalcMessage] = useState('');
  const [calcYear, setCalcYear] = useState<number | null>(null);

  const fetchAvailableYears = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/v1/master-data', { headers: { Authorization: `Bearer ${token}` }, params: { per_page: 1000 } });
      if (response.data?.data) {
        const yearArray: number[] = [];
        response.data.data.forEach((item: any) => { if (!yearArray.includes(item.year)) yearArray.push(item.year); });
        const years = yearArray.sort((a, b) => b - a);
        setAvailableYears(years);
        if (years.length > 0 && !yearFilter) setYearFilter(years[0]);
      }
    } catch { setAvailableYears(Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 2 + i)); }
  };

  const fetchStatus = async () => {
    if (!yearFilter) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/v1/analytics/regression-analysis/status?year=${yearFilter}`, { headers: { Authorization: `Bearer ${token}` } });
      setStatus(res.data);
      // Always fetch results when status changes, regardless of status
      await fetchResults();
    }
    catch (err: any) { setError(err.response?.data?.message || 'Failed'); setLoading(false); }
  };

  const fetchResults = async () => {
    if (!yearFilter) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/v1/analytics/regression-analysis?year=${yearFilter}`, { headers: { Authorization: `Bearer ${token}` } });
      setResults(res.data.results || {});
      setSummary(res.data.summary || null);
      setLoading(false);
    }
    catch (err: any) {
      setResults({});
      setSummary(null);
      setLoading(false);
    }
  };

  const handleRunCalc = async () => {
    if (!yearFilter) return;
    setCalcYear(yearFilter);
    setCalcDialogOpen(true);
    setIsCalculating(true);
    setCalcProgress(10);
    setCalcMessage('Starting regression analysis...');

    try {
      const token = localStorage.getItem('token');

      // Start the calculation
      setCalcProgress(30);
      setCalcMessage('Processing categories...');
      await axios.post('/api/v1/analytics/regression-analysis/run', { year: yearFilter, force: true }, { headers: { Authorization: `Bearer ${token}` } });

      setCalcProgress(90);
      setCalcMessage('Finalizing results...');

      // Wait a moment for database to settle
      await new Promise(r => setTimeout(r, 500));

      // Fetch results
      setCalcProgress(100);
      setCalcMessage('Complete!');
      await fetchStatus();

      // Close dialog after showing complete message
      setTimeout(() => {
        setIsCalculating(false);
        setCalcDialogOpen(false);
      }, 500);
    }
    catch (err: any) {
      setError(err.response?.data?.message || 'Failed to run regression analysis');
      setIsCalculating(false);
      setCalcDialogOpen(false);
    }
  };

  useEffect(() => { fetchAvailableYears(); }, []);
  useEffect(() => { if (yearFilter) fetchStatus(); }, [yearFilter]);

  if (loading && yearFilter) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (!yearFilter) return <Box sx={{ textAlign: 'center', mt: 6 }}><Science sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} /><Typography variant="h6" color="text.secondary">Select a year to view regression analysis</Typography></Box>;

  const PageHeader = () => (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box><Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>Regression Analysis</Typography><Typography variant="body1" color="text.secondary">Statistical regression analysis for credit risk categories</Typography></Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Tooltip title="Export"><IconButton sx={{ color: 'primary.main' }}><GetApp /></IconButton></Tooltip>
          <Tooltip title="Refresh"><IconButton onClick={handleRunCalc} sx={{ color: 'primary.main' }}><Refresh /></IconButton></Tooltip>
        </Box>
      </Box>
    </Box>
  );

  if (status?.status === 'no_master_data') return <Box><PageHeader /><YearFilterCard yearFilter={yearFilter} availableYears={availableYears} onYearChange={setYearFilter} status={status} /><Alert severity="warning"><Typography variant="h6">No Master Data for Year {yearFilter}</Typography><Typography variant="body2">Please import master data first.</Typography></Alert></Box>;

  if (status?.status === 'needs_calculation') return <Box><PageHeader /><YearFilterCard yearFilter={yearFilter} availableYears={availableYears} onYearChange={setYearFilter} status={status} onRun={handleRunCalc} isCalculating={isCalculating} /><Card sx={{ mt: 4, textAlign: 'center' }}><CardContent sx={{ p: 4 }}><Science sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} /><Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>Regression Analysis Not Calculated</Typography><Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>Click "Run Regression" button above to start calculation for year {yearFilter}.</Typography></CardContent></Card><CalculationDialog open={calcDialogOpen} calcYear={calcYear} calcProgress={calcProgress} calcMessage={calcMessage} isCalculating={isCalculating} onClose={() => setCalcDialogOpen(false)} /></Box>;

  return (
    <Box>
      <PageHeader />
      <YearFilterCard yearFilter={yearFilter} availableYears={availableYears} onYearChange={setYearFilter} status={status} onRun={handleRunCalc} isCalculating={isCalculating} />
      <SummaryCards summary={summary} theme={theme} />
      <Grid container spacing={3}>
        {Object.entries(results).map(([cat, res]) => (
          <Grid item xs={12} md={6} lg={4} key={cat}><ResultCard result={res} /></Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default RegressionAnalysis;
